import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@breakingthelines/design-system";

import { LoadingSpinner } from "@/components/common/loading-spinner";
import { probeModeration, type ProbeModerationResponse } from "@/lib/admin-api";

import styles from "./moderation-probe.module.css";

const TIER_CLASS: Record<string, string> = {
  HIGH: styles.tierHigh,
  MEDIUM: styles.tierMedium,
  LOW: styles.tierLow,
  NONE: styles.tierNone,
};

function tierClass(tier?: string): string {
  return TIER_CLASS[(tier ?? "").toUpperCase()] ?? styles.tierNone;
}

function formatConfidence(value?: number): string {
  if (typeof value !== "number") return "—";
  return `${Math.round(value * 100)}%`;
}

export function ModerationProbePage() {
  const [text, setText] = useState("");
  const [rubric, setRubric] = useState("");
  const [showRubric, setShowRubric] = useState(false);

  const probe = useMutation({
    mutationFn: () => probeModeration(text.trim(), rubric.trim() || undefined),
  });

  const verdict: ProbeModerationResponse | undefined = probe.data;
  const canSubmit = text.trim().length > 0 && !probe.isPending;

  return (
    <div className={styles.page}>
      <div>
        <h1 className={styles.title}>Test rubric</h1>
        <p className={styles.subtitle}>
          Run the LLM moderation judge against any text and see the verdict. This is read-only — it
          never hides, deranks, or flags content. Use it to tune the rubric against real examples
          before auto-action is enabled.
        </p>
      </div>

      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) probe.mutate();
        }}
      >
        <label className={styles.label} htmlFor="probe-text">
          Content to judge
        </label>
        <textarea
          id="probe-text"
          className={styles.textarea}
          placeholder="Paste an article title + body, a thought, or any text…"
          rows={10}
          value={text}
          onChange={(event) => setText(event.currentTarget.value)}
        />

        <button type="button" className={styles.toggle} onClick={() => setShowRubric((v) => !v)}>
          {showRubric ? "− Hide rubric override" : "+ Rubric override (advanced)"}
        </button>
        {showRubric ? (
          <textarea
            className={styles.textarea}
            placeholder="Optional: paste a candidate rubric to test before shipping it. Leave blank to use the built-in rubric."
            rows={8}
            value={rubric}
            onChange={(event) => setRubric(event.currentTarget.value)}
          />
        ) : null}

        <div className={styles.actions}>
          <Button type="submit" disabled={!canSubmit}>
            {probe.isPending ? "Judging…" : "Run judge"}
          </Button>
          {probe.isPending ? <LoadingSpinner size={18} label="Running the judge…" /> : null}
        </div>
      </form>

      {probe.isError ? (
        <p className={styles.error} role="alert">
          {probe.error instanceof Error ? probe.error.message : "Probe failed"}
        </p>
      ) : null}

      {verdict ? (
        <section className={styles.verdictCard} aria-live="polite">
          <div className={styles.verdictHeader}>
            <span className={`${styles.tierBadge} ${tierClass(verdict.tier)}`}>
              {(verdict.tier ?? "—").toUpperCase()}
            </span>
            <span className={styles.verdictWord}>{verdict.verdict ?? "—"}</span>
            <span className={styles.confidence}>
              {formatConfidence(verdict.confidence)} confident
            </span>
          </div>
          <p className={styles.reason}>{verdict.reason || "No reason provided."}</p>
          <dl className={styles.meta}>
            <div>
              <dt>Recommended action</dt>
              <dd>{verdict.recommendedAction || "—"}</dd>
            </div>
            <div>
              <dt>Model</dt>
              <dd className={styles.mono}>{verdict.model || "—"}</dd>
            </div>
            <div>
              <dt>Rubric</dt>
              <dd>{verdict.rubricVersion || "—"}</dd>
            </div>
          </dl>
        </section>
      ) : null}
    </div>
  );
}
